import { type FC, useEffect, useState } from "react"
import { Loader2 } from "lucide-react"

interface ActivateAccountHubspotFormProps {
  onSubmit: () => void
}

const SCRIPT_SRC = "https://js.hsforms.net/forms/embed/v2.js"
const PORTAL_ID = "4992313"
const FORM_ID = "acefa00c-64ea-4dc4-8f96-e92d037b5b67"
const TARGET_FORM_CONTAINER = "hubspotForm"

type HubspotWindow = Window & {
  hbspt?: {
    forms?: {
      create: (params: {
        portalId: string
        formId: string
        target: string
        css: string
        onFormSubmitted: () => void
      }) => void
    }
  }
}

const ActivateAccountHubspotForm: FC<ActivateAccountHubspotFormProps> = ({ onSubmit }) => {
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    setIsLoading(true)

    let scriptRef: HTMLScriptElement | null = null
    let isMounted = true

    const createForm = () => {
      const hubspot = (window as HubspotWindow).hbspt
      if (!hubspot?.forms?.create) return false

      const container = document.getElementById(TARGET_FORM_CONTAINER)
      if (!container) return false
      container.replaceChildren()

      hubspot.forms.create({
        portalId: PORTAL_ID,
        formId: FORM_ID,
        target: `#${TARGET_FORM_CONTAINER}`,
        css: `
          .hs-form {
            width: 100%;
            max-width: none;
            margin: 0 auto;
            font-family: inherit;
            font-size: 0.875rem;
            line-height: 1.25rem;
            color: var(--color-foreground);
          }
          .hs-form fieldset {
            width: 100% !important;
            max-width: none !important;
            margin: 0 !important;
            padding: 0 !important;
          }
          .hs-form fieldset.form-columns-1 .hs-form-field,
          .hs-form fieldset.form-columns-2 .hs-form-field,
          .hs-form fieldset.form-columns-3 .hs-form-field,
          .hs-form .hs-form-field {
            width: 100% !important;
            float: none !important;
            clear: both !important;
            max-width: none !important;
          }
          .hs-form .hs-form-field {
            margin-top: 0.95rem;
          }
          .hs-form label {
            display: block;
            margin-bottom: 0.45rem;
            text-align: left;
            font-size: 0.875rem;
            font-weight: 500;
            color: var(--color-muted-foreground);
          }
          .hs-form .hs-input {
            width: 100%;
            height: 2.8rem;
            border-radius: var(--radius-md);
            border: 1px solid var(--color-input);
            background: var(--color-background);
            color: var(--color-foreground);
            font-size: 0.875rem;
            padding: 0.5rem 0.75rem;
            outline: none;
          }
          .hs-form .hs-input:focus {
            border-color: var(--color-ring);
            box-shadow: 0 0 0 1px var(--color-ring);
          }
          .hs-form .input input.invalid {
            border-color: var(--color-destructive);
            box-shadow: none;
          }
          .hs-form .hs-error-msg {
            display: none;
            margin-top: 0.35rem;
            margin-bottom: 0;
            font-size: 0.8125rem;
            color: var(--color-destructive);
          }
          .hs-form .error .hs-error-msg {
            display: block;
          }
          .hs-form .hs-button.primary {
            width: 100%;
            margin-top: 1.1rem;
            min-height: 2.8rem;
            border: 0;
            border-radius: var(--radius-md);
            background: var(--color-primary);
            color: var(--color-primary-foreground);
            font-size: 0.875rem;
            font-weight: 600;
            cursor: pointer;
            transition: opacity 0.2s ease;
          }
          .hs-form .hs-button.primary:hover {
            opacity: 0.92;
          }
          .hs-form .hs-button.primary:focus {
            outline: none;
            box-shadow: 0 0 0 2px var(--color-ring);
          }
          .hs-form .hs_error_rollup,
          .hs-form .inputs-list label a {
            display: none;
          }
        `,
        onFormSubmitted: onSubmit,
      })

      return true
    }

    const maybeCreateForm = () => {
      const created = createForm()
      if (created && isMounted) setIsLoading(false)
    }

    const hubspot = (window as HubspotWindow).hbspt
    if (hubspot?.forms?.create) {
      maybeCreateForm()
    } else {
      scriptRef = document.createElement("script")
      scriptRef.src = SCRIPT_SRC
      scriptRef.async = true
      scriptRef.onload = maybeCreateForm
      document.body.appendChild(scriptRef)
    }

    return () => {
      isMounted = false

      if (scriptRef) scriptRef.onload = null
      if (scriptRef?.parentNode) scriptRef.parentNode.removeChild(scriptRef)
    }
  }, [onSubmit])

  return (
    <div className="relative w-full min-h-[260px]">
      <div id={TARGET_FORM_CONTAINER} className="w-full" />
      {isLoading && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      )}
    </div>
  )
}

export default ActivateAccountHubspotForm
